import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectIdentity } from '../../store/entities/identity';
import { selectTourniquetIdentity, tourniquetIdentityActions } from '../../store/entities/tourniquetIdentity';
import { getLangNameObj } from '../../utils/format';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import { TourniquetDialog } from './TourniquetDialog';
import { selectBusinessSettings } from '../../store/entities/bussinesSettings';
import { visitationService } from '@api/services/services';
import { hasRole } from '../../utils/roles';
import { QcsError } from '@api/models/QcsError';
import { SpzInput } from '../common/form/SpzInput';
import { Form, Formik, useFormikContext } from 'formik';
import { SafetyEquipmentDto } from '@api/models/invitation/SafetyEquipmentDto';
import { InvitationDto } from '@api/models/invitation/InvitationDto';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { VisitationEntryDto } from '@api/models/visitations/VisitationEntryDto';
import { UserRole } from '@api/models/enums/UserRole';
import { VisitationType } from '@api/models/enums/VisitationType';
import { resources } from '@api/resources';
import { doNothing } from '../../utils/shared';

interface Props {
  invitation: InvitationDto;
  person: InvitedPersonDto;
}

interface SpzFormFieldProps {
  onSpzChange: (spz: string) => void;
  onSpzBlur: (spz: string) => void;
  setFieldErrorRef: React.RefObject<((field: string, message: string | undefined) => void) | null>;
}

const SpzFormField: FC<SpzFormFieldProps> = ({ onSpzChange, onSpzBlur, setFieldErrorRef }) => {
  const { values, setFieldError } = useFormikContext<{ spz: string }>();

  useEffect(() => {
    setFieldErrorRef.current = setFieldError;
  }, [setFieldError, setFieldErrorRef]);

  useEffect(() => {
    onSpzChange(values.spz);
  }, [values.spz, onSpzChange]);

  const handleBlur = () => {
    onSpzBlur(values.spz);
  };

  return <SpzInput name="spz" onBlur={handleBlur} />;
};

export const TourniquetInvitationOk: FC<Props> = ({ invitation, person }) => {
  const { t, i18n } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const tourniquetIdentity = useAppSelector(selectTourniquetIdentity);
  const dispatch = useAppDispatch();
  const businessSettings = useAppSelector(selectBusinessSettings);
  const identity = useAppSelector(selectIdentity);
  const [postValidationErrors, setPostValidationErrors] = useState<QcsError[]>([]);
  const [spz, setSpz] = useState<string>('');
  const setFieldErrorRef = useRef<((field: string, message: string | undefined) => void) | null>(null);

  const safetyEq = ([] as SafetyEquipmentDto[]).concat(
    ...person.workplaces
      .map((x) => invitation.workplaces?.find((y) => y.workplace?.id === x.id))
      .filter((x) => !!x)
      .map((x) => x?.safetyEquipments ?? []),
  );

  const isTrainingRole = useMemo(() => {
    return hasRole(identity.roles, [UserRole.Training]);
  }, [identity.roles]);

  const handleConfirm = async () => {
    // Validate SPZ before proceeding
    if (spz) {
      const data: VisitationEntryDto = {
        invitation: invitation.id!,
        pin: tourniquetIdentity.pin!,
        spz: spz,
      };
      const validateEntry = await visitationService.accessPointValidateEntry(data, false);
      const hasSpzError = validateEntry.data.some((error) => error.code === 'E015' || error.code === 'E016');
      if (hasSpzError) {
        setFieldErrorRef.current?.('spz', t('tourniquet.spzEntryNotAllowed'));
        return;
      }
    }

    const data: VisitationEntryDto = {
      invitation: invitation.id!,
      pin: tourniquetIdentity.pin!,
      spz: spz,
    };

    // if is role training do not call visit, just validate the entry
    if (businessSettings.turnstilesEnabled && isTrainingRole) {
      const validateEntry = await visitationService.accessPointValidateEntry(data, true);
      setPostValidationErrors(validateEntry.data);
      setModalVisible(validateEntry.data.length === 0);
    } else {
      if (!isTrainingRole) {
        await visitationService.visit(data);
      }
      setModalVisible(true);
    }
  };

  const validateAgain = async () => {
    dispatch(
      tourniquetIdentityActions.success({
        ...tourniquetIdentity,
        validationErrors: postValidationErrors,
      }),
    );
  };

  const handleModalClose = () => {
    dispatch(tourniquetIdentityActions.default());
  };

  const handleSpzChange = (newSpz: string) => {
    setSpz(newSpz);
  };

  const handleSpzBlur = async (newSpz: string) => {
    if (!newSpz) {
      setFieldErrorRef.current?.('spz', undefined);
      return;
    }

    const data: VisitationEntryDto = {
      invitation: invitation.id!,
      pin: tourniquetIdentity.pin!,
      spz: newSpz,
    };
    const validateEntry = await visitationService.accessPointValidateEntry(data, false);
    const hasSpzError = validateEntry.data.some((error) => error.code === 'E015' || error.code === 'E016');
    if (hasSpzError) {
      setFieldErrorRef.current?.('spz', t('tourniquet.spzEntryNotAllowed'));
    } else {
      setFieldErrorRef.current?.('spz', undefined);
    }
  };

  return (
    <>
      {modalVisible && <TourniquetDialog mode={VisitationType.Enter} onClose={handleModalClose} />}

      <div style={{ width: '100%' }}>
        {postValidationErrors.length > 0 ? (
          <>
            <QcsTypography>{t('tourniquet.postValidationError')}</QcsTypography>
            <QcsLoadingButton
              variant="contained"
              onClick={validateAgain}
              resourceLoading={[resources.accessPointValidateEntry, resources.readPin]}
              sx={{ display: 'block', margin: '1rem auto' }}
            >
              {t('tourniquet.checkAgain')}
            </QcsLoadingButton>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <QcsTypography>{t('tourniquet.noIssue1')}</QcsTypography>
              <ul style={{ paddingLeft: '2rem' }}>
                {person.workplaces.map((x) => (
                  <li key={x.id}>{getLangNameObj(i18n, x)}</li>
                ))}
              </ul>
            </div>
            {safetyEq.length === 0 ? undefined : (
              <div style={{ marginBottom: '1rem' }}>
                <QcsTypography>{t('tourniquet.noIssue2')}</QcsTypography>
                <ul style={{ paddingLeft: '2rem' }}>
                  {safetyEq.map((x) => (
                    <li key={x.id}>{getLangNameObj(i18n, x)}</li>
                  ))}
                </ul>
              </div>
            )}

            {!hasRole(identity.roles, [UserRole.Training]) && (
              <div style={{ marginBottom: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center' }}>
                <QcsTypography>{t('tourniquet.carSpzPrompt')}:</QcsTypography>
                <Formik initialValues={{ spz: '' }} onSubmit={doNothing}>
                  <Form>
                    <SpzFormField onSpzChange={handleSpzChange} onSpzBlur={handleSpzBlur} setFieldErrorRef={setFieldErrorRef} />
                  </Form>
                </Formik>
              </div>
            )}

            <QcsLoadingButton
              variant="contained"
              onClick={handleConfirm}
              resourceLoading={[resources.accessPointValidateEntry, resources.readPin]}
              sx={{ display: 'block', margin: '0 auto' }}
            >
              {t(isTrainingRole ? 'common.close' : 'tourniquet.enterPin')}
            </QcsLoadingButton>
          </>
        )}
      </div>
    </>
  );
};
